import React, { useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen } from "lucide-react";
import { Deck } from "@/types/linguadeck";
import { fetchSharedDeck } from "@/lib/db";
import { useSafeAsyncData } from "@/hooks/useSafeAsyncData";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";
import MatchMode from "@/components/MatchMode";
import CatMouseMode from "@/components/study/CatMouseMode";
import DefenderMode from "@/components/study/DefenderMode";
import TowerMode from "@/components/study/TowerMode";
import MemoryMixMode from "@/components/study/MemoryMixMode";
import CipherRebusMode from "@/components/study/CipherRebusMode";
import IqMode from "@/components/study/IqMode";
import SoundCloudsMode from "@/components/study/SoundCloudsMode";
import MemoryJumbleMode from "@/components/study/MemoryJumbleMode";
import BlitzRushMode from "@/components/study/BlitzRushMode";
import SwipeArenaMode from "@/components/study/SwipeArenaMode";
import SecretMosaicMode from "@/components/study/SecretMosaicMode";
import { resolveSharedDeckToken } from "@/lib/sharedLinks";

const GAME_IDS = [
  "match",
  "cat-mouse",
  "defender",
  "tower",
  "memory-jumble",
  "memory",
  "rebus",
  "iq",
  "audio",
  "blitz",
  "swipe",
  "mosaic",
] as const;

type GameId = (typeof GAME_IDS)[number];

function isGameId(s: string): s is GameId {
  return (GAME_IDS as readonly string[]).includes(s);
}

export default function SharedDeckGamePage() {
  const { t } = useTranslation();
  const { token: routeToken, gameMode } = useParams<{ token: string; gameMode: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const publicHomeHref = user ? "/" : "/landing";
  const routeVariant = location.pathname.startsWith("/shared/") ? "shared" : "share";
  const token = resolveSharedDeckToken({ routeToken, pathname: location.pathname, searchParams }).token;

  const { state, isLoading, hasError } = useSafeAsyncData<Deck | null>({
    fetch: () => (token ? fetchSharedDeck(token) : Promise.resolve(null)),
    deps: [token, routeVariant],
    emptyIsSuccess: true,
  });

  const deck = state.status === "success" && state.data ? state.data : null;
  const loading = isLoading;
  const notFound = !token || !gameMode || (state.status === "success" && state.data == null) || hasError;
  const invalidGame = !isGameId(gameMode ?? "");

  useEffect(() => {
    const canonicalToken =
      typeof deck?.shareToken === "string" && deck.shareToken.trim() !== ""
        ? deck.shareToken.trim()
        : null;
    if (!canonicalToken || !token || canonicalToken === token || !gameMode) return;

    navigate(`/${routeVariant}/${canonicalToken}/study/${gameMode}`, { replace: true });
  }, [deck?.shareToken, gameMode, navigate, routeVariant, token]);

  const backToGames = useCallback(() => {
    navigate(token ? `/${routeVariant}/${token}?mode=games` : "/", { replace: true });
  }, [navigate, routeVariant, token]);

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-[hsl(var(--bg))] p-6">
        <div className="text-4xl mb-4">🎮</div>
        <p className="text-[hsl(var(--muted-foreground))]">{t("shared.loadingDeck")}</p>
      </div>
    );
  }

  if (notFound || !deck || invalidGame) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-[hsl(var(--bg))] p-6 text-center">
        <div className="text-4xl mb-4">🔒</div>
        <p className="font-semibold text-[hsl(var(--foreground))] mb-2">{t("shared.deckNotFound")}</p>
        <Link
          to={publicHomeHref}
          className="rounded-full bg-[hsl(var(--primary))] text-white px-6 py-2.5 text-sm font-medium no-underline"
        >
          {t("shared.goToLinguaDeck")}
        </Link>
      </div>
    );
  }

  const cards = deck.cards ?? [];
  const lang = deck.lang ?? "en";
  const gameId = gameMode as GameId;

  const header = (
    <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--bg2))]/95 pt-[env(safe-area-inset-top)]">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <Link to={token ? `/${routeVariant}/${token}?mode=games` : "/"} className="flex min-w-0 items-center gap-3 no-underline">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl sm:h-9 sm:w-9"
            style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent2)))" }}
          >
            <BookOpen className="h-4 w-4 text-white sm:h-5 sm:w-5" />
          </div>
          <span className="truncate text-lg font-bold text-[hsl(var(--foreground))]">StudyDo</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher compact />
          <ThemeToggle compact />
        </div>
      </div>
    </div>
  );

  if (cards.length === 0) {
    return (
      <div className="min-h-[100dvh] flex flex-col bg-[hsl(var(--bg))]" style={{ fontFamily: "DM Sans, sans-serif" }}>
        {header}
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center text-[hsl(var(--muted-foreground))]">
          <p>{t("home.deckEmpty")}</p>
          <button
            type="button"
            onClick={backToGames}
            className="rounded-full bg-[hsl(var(--primary))] text-white px-6 py-2.5 text-sm font-medium"
          >
            ← {t("action.back")}
          </button>
        </div>
      </div>
    );
  }

  const noop = () => {};

  if (gameId === "match") {
    return (
      <div
        className="fixed inset-0 z-20 flex h-[100dvh] w-full flex-col bg-[hsl(var(--bg))] pt-4 overflow-hidden"
        style={{ fontFamily: "DM Sans, sans-serif" }}
      >
        {header}
        <div className="flex flex-1 min-h-0 flex-col items-center w-full overflow-hidden">
          <div className="h-full w-full max-w-4xl flex flex-col overflow-hidden">
            <MatchMode
              key={`shared-match-${deck.id}`}
              cards={cards}
              onBack={backToGames}
              onProgressComplete={noop}
            />
          </div>
        </div>
      </div>
    );
  }

  const gameBackButton = (
    <button
      type="button"
      onClick={backToGames}
      className="mb-6 flex items-center gap-2 px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] font-medium transition-colors"
    >
      ← {t("action.back")}
    </button>
  );

  return (
    <div
      className="min-h-[100dvh] flex flex-col bg-[hsl(var(--bg))] pb-8"
      style={{ fontFamily: "DM Sans, sans-serif" }}
    >
      {header}
      <div className="flex flex-1 flex-col items-center w-full px-4 pt-4">
        {gameBackButton}
        {gameId === "cat-mouse" && (
          <CatMouseMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
        {gameId === "defender" && (
          <DefenderMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
        {gameId === "tower" && (
          <TowerMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
        {gameId === "memory-jumble" && (
          <MemoryJumbleMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
        {gameId === "memory" && (
          <MemoryMixMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
        {gameId === "rebus" && (
          <CipherRebusMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
        {gameId === "iq" && (
          <IqMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
        {gameId === "audio" && (
          <SoundCloudsMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
        {gameId === "blitz" && (
          <BlitzRushMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
        {gameId === "swipe" && (
          <SwipeArenaMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
        {gameId === "mosaic" && (
          <SecretMosaicMode
            cards={cards}
            lang={lang}
            onBack={backToGames}
            onProgressComplete={noop}
          />
        )}
      </div>
    </div>
  );
}
